import { useCallback, useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  getNode,
  listNodeFiles,
  deleteNodeFileLocal,
  listNodeCommands,
  getNodeCameras,
  updateNode,
  updateRecordingAutoUpload,
  runRecordingAutoUploadNow,
} from '@/api/nodes'
import {
  createNodeModelDeployment,
  deleteNodeModelMapping,
  listNodeAvailableModels,
  listNodeModelStates,
  materializeNodeModelCameras,
  refreshModelStatus,
  upsertNodeModelMapping,
} from '@/api/models'
import { listJobs } from '@/api/jobs'
import { useAuth } from '@/auth/useAuth'
import { usePolling } from '@/hooks/usePolling'
import type {
  CameraInfo,
  NodeDetail,
  RecordedFile,
  CommandSummary,
  JobDetail,
  ModelStateItem,
  NodeAvailableModel,
  UpdateRecordingAutoUploadRequest,
} from '@/types'
import NodeStatusBadge from '@/components/NodeStatusBadge'
import ResourcePanel from '@/components/ResourcePanel'
import CommandHistory from '@/components/CommandHistory'
import FileList from '@/components/FileList'
import LogTail from '@/components/LogTail'
import CommandModal, { type ModalCommandType } from '@/components/CommandModal'
import TokenRotateModal from '@/components/TokenRotateModal'
import AgentUpdateModal from '@/components/AgentUpdateModal'
import {
  ActionBtn,
  CameraPanel,
  CurrentJobs,
  cx,
  formatTime,
  getNodesReturnPath,
  InfoRow,
  RecordingAutoUploadModal,
  RecordingAutoUploadPanel,
  ModelDeployPanel,
  GuardianAlphaRestartButton,
  GuardianAlphaStatusPanel,
  NodeDetailTabs,
  normalizedRecordingStatus,
  parseCapabilities,
  relativeTime,
  type NodeDetailTab,
} from '@/features/node-detail'
import styles from '@/features/node-detail/nodeDetail.module.css'
import NodeDangerZone from '@/features/node-detail/NodeDangerZone'

interface Toast {
  id: number
  message: string
}

let toastSeq = 0
const STOPPING_OPTIMISTIC_TTL_MS = 45_000
const DELETED_FILES_PAGE_SIZE = 10
const DELETED_FILES_RETENTION_NOTICE = 'Deleted recordings are shown for up to 30 days.'

export default function NodeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const [node, setNode] = useState<NodeDetail | null>(null)
  const [files, setFiles] = useState<RecordedFile[]>([])
  const [inferenceFiles, setInferenceFiles] = useState<RecordedFile[]>([])
  const [deletedManualFiles, setDeletedManualFiles] = useState<RecordedFile[]>([])
  const [deletedInferenceFiles, setDeletedInferenceFiles] = useState<RecordedFile[]>([])
  const [deletedManualTotal, setDeletedManualTotal] = useState(0)
  const [deletedInferenceTotal, setDeletedInferenceTotal] = useState(0)
  const [deletedManualPage, setDeletedManualPage] = useState(1)
  const [deletedInferencePage, setDeletedInferencePage] = useState(1)
  const [selectedDeletedManualCameraKey, setSelectedDeletedManualCameraKey] = useState<string | null>(null)
  const [selectedDeletedInferenceCameraKey, setSelectedDeletedInferenceCameraKey] = useState<string | null>(null)
  const [deletedManualExpanded, setDeletedManualExpanded] = useState(false)
  const [deletedInferenceExpanded, setDeletedInferenceExpanded] = useState(false)
  const [deletedFilesError, setDeletedFilesError] = useState<string | null>(null)
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)
  const [commands, setCommands] = useState<CommandSummary[]>([])
  const [runningJobs, setRunningJobs] = useState<JobDetail[]>([])
  const [cameras, setCameras] = useState<CameraInfo[]>([])
  const [nodeAvailableModels, setNodeAvailableModels] = useState<NodeAvailableModel[]>([])
  const [modelStates, setModelStates] = useState<ModelStateItem[]>([])
  const [modelLoading, setModelLoading] = useState(false)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelToolbarBusy, setModelToolbarBusy] = useState<'refresh' | 'reload' | null>(null)
  const modelFetchSequence = useRef(0)
  const deletedFilesFetchSequence = useRef(0)
  const [loadingNode, setLoadingNode] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modal, setModal] = useState<ModalCommandType | null>(null)
  const [modalCameraKey, setModalCameraKey] = useState<string | undefined>(undefined)
  const [uploadSourceKind, setUploadSourceKind] = useState<'recording' | 'inference_recording'>('recording')
  const [initialUploadFileIds, setInitialUploadFileIds] = useState<string[]>([])
  const [selectedManualUploadFileIds, setSelectedManualUploadFileIds] = useState<string[]>([])
  const [selectedInferenceUploadFileIds, setSelectedInferenceUploadFileIds] = useState<string[]>([])
  const [uploadCameraKey, setUploadCameraKey] = useState<string | null | undefined>(undefined)
  const [activeTab, setActiveTab] = useState<NodeDetailTab>('overview')
  const [showRotateToken, setShowRotateToken] = useState(false)
  const [showAgentUpdate, setShowAgentUpdate] = useState(false)
  const [showAutoUploadConfig, setShowAutoUploadConfig] = useState(false)
  const [autoUploadRunning, setAutoUploadRunning] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [isEditingMetadata, setIsEditingMetadata] = useState(false)
  const [companyDraft, setCompanyDraft] = useState('')
  const [siteDraft, setSiteDraft] = useState('')
  const [metadataSaving, setMetadataSaving] = useState(false)
  const [metadataError, setMetadataError] = useState<string | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [pendingStoppingCameras, setPendingStoppingCameras] = useState<Map<string, number>>(() => new Map())
  const effectiveCameraKeys = cameras.map((camera) => camera.key)
  const effectiveCameraKeysKey = effectiveCameraKeys.join('\u001f')
  const allowedCameraKeys = new Set(effectiveCameraKeys)
  const effectiveDeletedManualCameraKey = selectedDeletedManualCameraKey
    && allowedCameraKeys.has(selectedDeletedManualCameraKey)
    ? selectedDeletedManualCameraKey
    : effectiveCameraKeys[0] ?? null
  const effectiveDeletedInferenceCameraKey = selectedDeletedInferenceCameraKey
    && allowedCameraKeys.has(selectedDeletedInferenceCameraKey)
    ? selectedDeletedInferenceCameraKey
    : effectiveCameraKeys[0] ?? null
  const visibleManualFiles = files.filter((file) => (
    file.camera_key != null && allowedCameraKeys.has(file.camera_key)
  ))
  const visibleInferenceFiles = inferenceFiles.filter((file) => (
    file.camera_key != null && allowedCameraKeys.has(file.camera_key)
  ))
  const visibleDeletedManualFiles = deletedManualFiles.filter((file) => (
    file.camera_key != null && allowedCameraKeys.has(file.camera_key)
  ))
  const visibleDeletedInferenceFiles = deletedInferenceFiles.filter((file) => (
    file.camera_key != null && allowedCameraKeys.has(file.camera_key)
  ))
  const uploadFilesForModal = uploadSourceKind === 'inference_recording'
    ? visibleInferenceFiles
    : visibleManualFiles
  const filteredUploadFilesForModal = uploadCameraKey === undefined
    ? uploadFilesForModal
    : uploadFilesForModal.filter((file) => file.camera_key === uploadCameraKey)

  const canEditNodeMetadata = hasPermission('node:metadata:write')
  const canControlRecording = hasPermission('recording:execute')
  const canUpload = hasPermission('upload:execute')
  const canDeleteLocal = hasPermission('upload:delete-local')
  const canManageAutoUpload = hasPermission('auto-upload:manage')
  const canRunAutoUpload = hasPermission('auto-upload:execute')
  const canRotateNodeToken = hasPermission('node-token:rotate')
  const canUpdateAgent = hasPermission('agent-update:execute')
  const canDeployModel = hasPermission('model-deployment:execute')
  const nodesReturnPath = getNodesReturnPath(location.state)

  function addToast(message: string) {
    const tid = ++toastSeq
    setToasts((t) => [...t, { id: tid, message }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== tid)), 4000)
  }

  function openUpload(
    kind: 'recording' | 'inference_recording',
    ids: string[],
    sourceFiles: RecordedFile[],
  ) {
    const selectedFile = sourceFiles.find((file) => file.id === ids[0])
    setUploadSourceKind(kind)
    setInitialUploadFileIds(ids)
    setUploadCameraKey(selectedFile?.camera_key)
    setModal('upload.start')
  }

  const fetchAll = useCallback(async () => {
    if (!id) return
    try {
      const [nodeData, filesData, commandsData, jobsData, camerasData] = await Promise.all([
        getNode(id),
        listNodeFiles(id, { local_state: 'present' }),
        listNodeCommands(id),
        listJobs({ node_id: id, status: 'running' }),
        getNodeCameras(id),
      ])
      const inferenceFilesData = await listNodeFiles(id, {
        file_kind: 'inference_recording',
        local_state: 'present',
      })
      setNode(nodeData)
      setFiles(filesData.items)
      setInferenceFiles(inferenceFilesData.items)
      setCommands(commandsData.items)
      setRunningJobs(jobsData.items as JobDetail[])
      setCameras(camerasData.items)
      setError(null)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Failed to load node data')
    } finally {
      setLoadingNode(false)
    }
  }, [id])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  usePolling(fetchAll, 20000, !loadingNode)

  useEffect(() => {
    const currentCameraKeys = new Set(
      effectiveCameraKeysKey ? effectiveCameraKeysKey.split('\u001f') : [],
    )
    setSelectedDeletedManualCameraKey((current) => (
      current && currentCameraKeys.has(current) ? current : null
    ))
    setSelectedDeletedInferenceCameraKey((current) => (
      current && currentCameraKeys.has(current) ? current : null
    ))
    setDeletedManualFiles([])
    setDeletedManualTotal(0)
    setDeletedInferenceFiles([])
    setDeletedInferenceTotal(0)
    setDeletedManualPage(1)
    setDeletedInferencePage(1)
    setDeletedFilesError(null)
  }, [effectiveCameraKeysKey])

  const fetchDeletedFiles = useCallback(async () => {
    const fetchSequence = ++deletedFilesFetchSequence.current
    if (!id) return
    const isManualTab = activeTab === 'camera_recordings'
    const isInferenceTab = activeTab === 'inference_recordings'
    if (!isManualTab && !isInferenceTab) return
    const selectedCameraKey = isManualTab
      ? effectiveDeletedManualCameraKey
      : effectiveDeletedInferenceCameraKey
    if (!selectedCameraKey) {
      if (isManualTab) {
        setDeletedManualFiles([])
        setDeletedManualTotal(0)
      } else {
        setDeletedInferenceFiles([])
        setDeletedInferenceTotal(0)
      }
      setDeletedFilesError(null)
      return
    }
    const page = isManualTab ? deletedManualPage : deletedInferencePage
    const fileKind = isManualTab ? 'recording' : 'inference_recording'
    try {
      const response = await listNodeFiles(id, {
        file_kind: fileKind,
        local_state: 'deleted',
        camera_keys: [selectedCameraKey],
        limit: DELETED_FILES_PAGE_SIZE,
        offset: (page - 1) * DELETED_FILES_PAGE_SIZE,
      })
      if (fetchSequence !== deletedFilesFetchSequence.current) return
      if (isManualTab) {
        setDeletedManualFiles(response.items)
        setDeletedManualTotal(response.total)
      } else {
        setDeletedInferenceFiles(response.items)
        setDeletedInferenceTotal(response.total)
      }
      setDeletedFilesError(null)
    } catch (e: unknown) {
      if (fetchSequence !== deletedFilesFetchSequence.current) return
      const err = e as { message?: string }
      setDeletedFilesError(err?.message ?? 'Failed to load deleted recordings')
    }
  }, [
    activeTab,
    deletedInferencePage,
    deletedManualPage,
    effectiveDeletedInferenceCameraKey,
    effectiveDeletedManualCameraKey,
    id,
  ])

  useEffect(() => {
    void fetchDeletedFiles()
  }, [fetchDeletedFiles])

  useEffect(() => () => {
    deletedFilesFetchSequence.current += 1
  }, [])

  usePolling(
    fetchDeletedFiles,
    20000,
    activeTab === 'camera_recordings' || activeTab === 'inference_recordings',
  )

  const fetchModelData = useCallback(async () => {
    if (!id) return
    const fetchSequence = ++modelFetchSequence.current
    setModelLoading(true)
    try {
      if (canDeployModel) {
        await materializeNodeModelCameras(id)
      }
      const [availableModelsData, statesData] = await Promise.all([
        listNodeAvailableModels(id),
        listNodeModelStates(id),
      ])
      if (fetchSequence !== modelFetchSequence.current) return
      setNodeAvailableModels(availableModelsData.items)
      setModelStates(statesData.items)
      setModelError(null)
    } catch (e: unknown) {
      if (fetchSequence !== modelFetchSequence.current) return
      const err = e as { message?: string }
      setModelError(err?.message ?? 'Failed to load model deployment data')
    } finally {
      if (fetchSequence === modelFetchSequence.current) {
        setModelLoading(false)
      }
    }
  }, [canDeployModel, id])

  useEffect(() => {
    if (activeTab === 'model_deploy') {
      fetchModelData()
    }
  }, [activeTab, fetchModelData])

  const modelDeployRunning = modelStates.some((state) => (
    state.deployment_status === 'deploying'
    || state.deployment_status === 'restart_required'
    || state.pending_target_status === 'queued'
    || state.pending_target_status === 'running'
  ))
  usePolling(fetchModelData, modelDeployRunning ? 5000 : 30000, activeTab === 'model_deploy')

  useEffect(() => {
    setPendingStoppingCameras((prev) => {
      if (prev.size === 0) return prev
      const cameraStatusByKey = new Map(
        cameras.map((camera) => [camera.key, normalizedRecordingStatus(camera.recording_status)]),
      )
      const now = Date.now()
      const next = new Map<string, number>()
      prev.forEach((requestedAt, cameraKey) => {
        const status = cameraStatusByKey.get(cameraKey)
        const isFresh = now - requestedAt < STOPPING_OPTIMISTIC_TTL_MS
        if (status === 'stopping' || (status === 'running' && isFresh)) {
          next.set(cameraKey, requestedAt)
        }
      })
      if (next.size === prev.size && [...next].every(([cameraKey, requestedAt]) => prev.get(cameraKey) === requestedAt)) {
        return prev
      }
      return next
    })
  }, [cameras])

  // Use command.log_tail directly (backend cycle 4: CommandSummary includes log_tail)
  const logToShow = commands.length > 0 ? (commands[0].log_tail ?? null) : null

  function startNameEdit() {
    setNameDraft(node?.name ?? '')
    setNameError(null)
    setIsEditingName(true)
  }

  function cancelNameEdit() {
    setNameDraft(node?.name ?? '')
    setNameError(null)
    setIsEditingName(false)
  }

  async function saveNodeName() {
    if (!id) return
    setNameSaving(true)
    setNameError(null)
    try {
      const trimmed = nameDraft.trim()
      const updated = await updateNode(id, { name: trimmed || null })
      setNode((current) => current ? { ...current, ...updated } : current)
      setIsEditingName(false)
      addToast('Node name updated.')
      await fetchAll()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setNameError(err?.message ?? 'Failed to update node name')
    } finally {
      setNameSaving(false)
    }
  }

  function startMetadataEdit() {
    setCompanyDraft(node?.company ?? '')
    setSiteDraft(node?.site ?? '')
    setMetadataError(null)
    setIsEditingMetadata(true)
  }

  function cancelMetadataEdit() {
    setCompanyDraft(node?.company ?? '')
    setSiteDraft(node?.site ?? '')
    setMetadataError(null)
    setIsEditingMetadata(false)
  }

  async function saveNodeMetadata() {
    if (!id) return
    setMetadataSaving(true)
    setMetadataError(null)
    try {
      const company = companyDraft.trim()
      const site = siteDraft.trim()
      const updated = await updateNode(id, {
        company: company || null,
        site: site || null,
      })
      setNode((current) => current ? { ...current, ...updated } : current)
      setIsEditingMetadata(false)
      addToast('Node grouping updated.')
      await fetchAll()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setMetadataError(err?.message ?? 'Failed to update node grouping')
    } finally {
      setMetadataSaving(false)
    }
  }

  async function saveRecordingAutoUpload(body: UpdateRecordingAutoUploadRequest) {
    if (!id) return
    const updated = await updateRecordingAutoUpload(id, body)
    setNode((current) => current ? { ...current, recording_auto_upload: updated } : current)
    addToast(updated.mode === 'off' ? 'Recording auto upload disabled.' : `${updated.mode} auto upload enabled.`)
    await fetchAll()
  }

  async function runRecordingAutoUpload() {
    if (!id || autoUploadRunning) return
    const current = node?.recording_auto_upload
    const selected = current?.mode === 'manual' || current?.mode === 'inference'
      ? current[current.mode]
      : null
    if (selected?.delete_local_after_success) {
      const ok = window.confirm('전날 녹화 파일 업로드를 지금 실행합니다. 업로드 성공 파일은 노드에서 삭제됩니다. 진행할까요?')
      if (!ok) return
    }
    setAutoUploadRunning(true)
    try {
      const res = await runRecordingAutoUploadNow(id)
      addToast(
        res.commands_created > 0
          ? `${res.mode} auto upload command queued (${res.commands_created}).`
          : 'No eligible previous-day files, or upload command already exists.',
      )
      await fetchAll()
    } catch (e: unknown) {
      const err = e as { message?: string }
      addToast(err?.message ?? 'Failed to run recording auto upload.')
    } finally {
      setAutoUploadRunning(false)
    }
  }

  async function handleDeleteLocal(file: RecordedFile) {
    if (!id || deletingFileId || file.local_path == null || file.upload_status !== 'uploaded') return
    const confirmed = window.confirm([
      `Delete ${file.file_name ?? 'this file'} from the node?`,
      'The uploaded NAS copy is not affected, but the local file cannot be recovered.',
    ].join('\n\n'))
    if (!confirmed) return

    setDeletingFileId(file.id)
    const updateFileState = (items: RecordedFile[]) => items.map((item) => (
      item.id === file.id ? { ...item, delete_status: 'deleting' } : item
    ))
    if (file.file_kind === 'inference_recording') {
      setInferenceFiles(updateFileState)
    } else {
      setFiles(updateFileState)
    }

    try {
      await deleteNodeFileLocal(id, file.id)
      addToast('Local file deletion queued.')
      await fetchAll()
    } catch (e: unknown) {
      const err = e as { message?: string }
      addToast(err?.message ?? 'Failed to delete the local file.')
      await fetchAll()
    } finally {
      setDeletingFileId(null)
    }
  }

  async function handleMapModel(cameraKey: string, modelArtifactId: string) {
    if (!id) return
    await upsertNodeModelMapping(id, cameraKey, modelArtifactId)
    addToast('Model mapping saved.')
    await fetchModelData()
  }

  async function handleDeleteModelMapping(cameraKey: string) {
    if (!id) return
    await deleteNodeModelMapping(id, cameraKey)
    setModelStates((current) => current.map((state) => (
      state.camera_key === cameraKey
        ? {
            ...state,
            desired_model_artifact_id: null,
            desired_model_key: null,
            desired_version: null,
          }
        : state
    )))
    await fetchModelData()
  }

  async function handleRefreshModelStatus(slotKey?: string | null) {
    if (!id) return
    await refreshModelStatus(id, slotKey)
    addToast('Model status fetch queued.')
    await fetchAll()
    await fetchModelData()
  }

  async function handleDeployModel(state: ModelStateItem) {
    if (!id) return
    await createNodeModelDeployment(id, state.camera_key, `Dashboard model deploy for ${state.camera_key}`)
    addToast('Model deployment queued.')
    await fetchAll()
    await fetchModelData()
  }

  async function runModelToolbarAction(
    action: 'refresh' | 'reload',
    callback: () => Promise<void>,
  ) {
    if (modelToolbarBusy) return
    setModelToolbarBusy(action)
    try {
      await callback()
    } catch (e: unknown) {
      const err = e as { message?: string }
      addToast(err?.message ?? 'Model action failed.')
    } finally {
      setModelToolbarBusy(null)
    }
  }

  if (loadingNode) {
    return (
      <div>
        <p className={styles.pageMessage}>Loading node...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className={styles.errorBanner}>
          {error}
          <button onClick={fetchAll} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!node) return null

  // Multiple cameras may be recording simultaneously
  const recordingJobs = runningJobs.filter((j) => j.type.startsWith('recording'))
  const uploadJob = runningJobs.find((j) => j.type.startsWith('upload'))
  const activeUploadCommand = commands.find((command) => (
    command.type === 'upload.start'
    && ['queued', 'dispatched', 'accepted', 'running'].includes(command.status)
  ))
  const isUploading = Boolean(uploadJob || activeUploadCommand)
  const capabilities = parseCapabilities(node.capabilities)
  const supportsRecordingControl = capabilities.has('recording.managed_recording')
  const supportsInferenceFiles = capabilities.has('inference_recording.files')
  const supportsInferenceUpload = capabilities.has('inference_recording.upload')
  const supportsFileDelete = capabilities.has('recording.file-delete')
  const supportsModelDeploy = capabilities.has('model.deploy.slot-symlink')
  const displayCameras = cameras.map((camera) => {
    if (!pendingStoppingCameras.has(camera.key)) return camera
    const status = normalizedRecordingStatus(camera.recording_status)
    if (status !== 'running' && status !== 'stopping') return camera
    return { ...camera, recording_status: 'stopping' as const }
  })
  void recordingJobs

  return (
    <div>
      <div className={styles.pageHeader}>
        <Link to={nodesReturnPath} className={styles.backIconButton} aria-label="Back to nodes" title="Back to nodes">
          <span className={styles.backChevron} />
        </Link>
        {isEditingName ? (
          <div className={styles.nameEditRow}>
            <input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !nameSaving) saveNodeName()
                if (e.key === 'Escape' && !nameSaving) cancelNameEdit()
              }}
              maxLength={255}
              disabled={nameSaving}
              autoFocus
              placeholder={node.node_key}
              className={styles.nameInput}
            />
            <button onClick={saveNodeName} disabled={nameSaving} className={styles.primaryButton}>
              {nameSaving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={cancelNameEdit} disabled={nameSaving} className={styles.secondaryButton}>
              Cancel
            </button>
            {nameError && <span className={styles.nameError}>{nameError}</span>}
          </div>
        ) : (
          <>
            <h1 className={styles.pageTitle}>
              {node.name ?? node.node_key}
            </h1>
            {canEditNodeMetadata && (
              <button onClick={startNameEdit} className={styles.secondaryButton}>
                Edit name
              </button>
            )}
          </>
        )}
        <NodeStatusBadge status={node.status} />
        <div className={styles.headerSpacer} />
        <div className={styles.headerActions}>
          {/* Recording is per-camera — see the Cameras section below. */}
          {canUpload && (
            <>
            <ActionBtn
              label="Start upload"
              disabled={isUploading}
              onClick={() => {
                if (activeTab === 'inference_recordings' && supportsInferenceUpload) {
                  openUpload(
                    'inference_recording',
                    selectedInferenceUploadFileIds,
                    visibleInferenceFiles,
                  )
                  return
                }
                openUpload(
                  'recording',
                  activeTab === 'camera_recordings' ? selectedManualUploadFileIds : [],
                  visibleManualFiles,
                )
              }}
              color="#2e7d32"
            />
            <ActionBtn
              label="Stop upload"
              disabled={!isUploading}
              onClick={() => setModal('upload.stop')}
              color="#c62828"
            />
            </>
          )}
          <ActionBtn
            label="Refresh files"
            disabled={false}
            onClick={fetchAll}
            color="#546e7a"
          />
          {canRotateNodeToken && (
            <ActionBtn
              label="Rotate token"
              disabled={false}
              onClick={() => setShowRotateToken(true)}
              color="#7b1fa2"
            />
          )}
          {canUpdateAgent && (
            <ActionBtn
              label="Update agent"
              disabled={false}
              onClick={() => setShowAgentUpdate(true)}
              color="#00838f"
            />
          )}
        </div>
      </div>

      <div className={styles.detailLayout}>
        <div className={styles.sideColumn}>
          <div className={styles.card}>
            <div className={styles.basicInfoHeader}>
              <div className={cx(styles.cardTitle, styles.cardTitleInline)}>Basic Info</div>
              {canEditNodeMetadata && !isEditingMetadata && (
                <button onClick={startMetadataEdit} className={styles.secondaryButton}>
                  Edit grouping
                </button>
              )}
            </div>
            <InfoRow label="Node key" value={node.node_key} />
            {isEditingMetadata ? (
              <div className={styles.metadataForm}>
                <label className={styles.metadataLabel}>
                  <span>Company</span>
                  <input
                    value={companyDraft}
                    onChange={(e) => setCompanyDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !metadataSaving) saveNodeMetadata()
                      if (e.key === 'Escape' && !metadataSaving) cancelMetadataEdit()
                    }}
                    maxLength={255}
                    disabled={metadataSaving}
                    placeholder="e.g. 삼표 인천"
                    className={styles.metadataInput}
                  />
                </label>
                <label className={styles.metadataLabel}>
                  <span>Site/area</span>
                  <input
                    value={siteDraft}
                    onChange={(e) => setSiteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !metadataSaving) saveNodeMetadata()
                      if (e.key === 'Escape' && !metadataSaving) cancelMetadataEdit()
                    }}
                    maxLength={255}
                    disabled={metadataSaving}
                    placeholder="e.g. packer"
                    className={styles.metadataInput}
                  />
                </label>
                <div className={styles.metadataActions}>
                  <button onClick={saveNodeMetadata} disabled={metadataSaving} className={styles.primaryButton}>
                    {metadataSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={cancelMetadataEdit} disabled={metadataSaving} className={styles.secondaryButton}>
                    Cancel
                  </button>
                  {metadataError && <span className={styles.nameError}>{metadataError}</span>}
                </div>
              </div>
            ) : (
              <>
                <InfoRow label="Company" value={node.company} />
                <InfoRow label="Site/area" value={node.site} />
              </>
            )}
            <InfoRow label="Hostname" value={node.hostname} />
            <InfoRow label="OS" value={node.os} />
            <InfoRow label="Agent version" value={node.agent_version} />
            <InfoRow label="Capabilities" value={node.capabilities} mono />
            <InfoRow label="Last heartbeat" value={relativeTime(node.last_seen_at)} />
            <InfoRow label="Registered" value={formatTime(node.created_at)} />
          </div>
          <ResourcePanel metrics={node.latest_metrics ?? null} />
          <NodeDangerZone node={node} canDelete={hasPermission('node:delete')}
            onDeleted={() => navigate(nodesReturnPath, { replace: true })} />
        </div>

        <div className={styles.mainColumn}>
          <NodeDetailTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            recordingJobCount={recordingJobs.length}
            uploading={isUploading}
            cameraFileCount={visibleManualFiles.length}
            inferenceFileCount={visibleInferenceFiles.length}
            modelAlertCount={modelStates.filter((state) => state.deployment_status === 'restart_required').length}
          />
          {activeTab === 'overview' && (
            <>
              <CurrentJobs recordingJobs={recordingJobs} uploadJob={uploadJob ?? null} />
              <GuardianAlphaStatusPanel node={node} />
              {cameras.length > 0 && (
                <CameraPanel
                  cameras={displayCameras}
                  canControlRecording={canControlRecording}
                  supportsRecordingControl={supportsRecordingControl}
                  onStartRecording={(key) => {
                    setModalCameraKey(key)
                    setModal('recording.start')
                  }}
                  onStopRecording={(key) => {
                    setModalCameraKey(key)
                    setModal('recording.stop')
                  }}
                />
              )}
              <CommandHistory commands={commands} />
              <LogTail log={logToShow} label="Recent log" />
            </>
          )}
          {activeTab === 'camera_recordings' && (
            <>
              <RecordingAutoUploadPanel
                state={node.recording_auto_upload}
                configureDisabled={!canManageAutoUpload || isUploading}
                runDisabled={!canRunAutoUpload || isUploading}
                runningNow={autoUploadRunning}
                onConfigure={() => setShowAutoUploadConfig(true)}
                onRunNow={runRecordingAutoUpload}
              />
              <FileList
                files={visibleManualFiles}
                title="Manual Recordings"
                emptyText={cameras.length === 0
                  ? 'No cameras are currently configured for this node.'
                  : 'No manual recording files found for the current camera configuration.'}
                showCamera
                showVideoMeta
                groupByCamera
                pageSize={10}
                cameraOptions={cameras.map((camera) => ({
                  key: camera.key,
                  label: camera.name ?? camera.key,
                }))}
                onUploadSelected={
                  canUpload
                    ? (ids) => openUpload('recording', ids, visibleManualFiles)
                    : undefined
                }
                uploadDisabled={isUploading}
                onSelectionChange={setSelectedManualUploadFileIds}
                onDeleteLocal={canDeleteLocal && supportsFileDelete ? handleDeleteLocal : undefined}
                deleteLocalDisabled={isUploading}
                deletingFileId={deletingFileId}
              />
              {deletedFilesError && <div className={styles.errorBanner}>{deletedFilesError}</div>}
              <FileList
                files={visibleDeletedManualFiles}
                title="Deleted Manual Recordings"
                description={DELETED_FILES_RETENTION_NOTICE}
                emptyText={cameras.length === 0
                  ? 'No cameras are currently configured for this node.'
                  : 'No deleted manual recordings found for the current camera configuration.'}
                showCamera
                showVideoMeta
                groupByCamera
                showDeletedAt
                defaultSortKey="deleted_at"
                pageSize={DELETED_FILES_PAGE_SIZE}
                cameraOptions={cameras.map((camera) => ({
                  key: camera.key,
                  label: camera.name ?? camera.key,
                }))}
                activeCameraKey={effectiveDeletedManualCameraKey}
                onCameraChange={(cameraKey) => {
                  setSelectedDeletedManualCameraKey(cameraKey)
                  setDeletedManualPage(1)
                  setDeletedManualFiles([])
                  setDeletedManualTotal(0)
                  setDeletedFilesError(null)
                }}
                showCameraCounts={false}
                collapsible
                expanded={deletedManualExpanded}
                onExpandedChange={setDeletedManualExpanded}
                serverPagination={{
                  page: deletedManualPage,
                  total: deletedManualTotal,
                  onPageChange: setDeletedManualPage,
                }}
              />
            </>
          )}
          {activeTab === 'inference_recordings' && (
            supportsInferenceFiles ? (
              <>
                <RecordingAutoUploadPanel
                  state={node.recording_auto_upload}
                  configureDisabled={!canManageAutoUpload || isUploading}
                  runDisabled={!canRunAutoUpload || isUploading}
                  runningNow={autoUploadRunning}
                  onConfigure={() => setShowAutoUploadConfig(true)}
                  onRunNow={runRecordingAutoUpload}
                />
                <FileList
                  files={visibleInferenceFiles}
                  title="Inference Recordings"
                  emptyText={cameras.length === 0
                    ? 'No cameras are currently configured for this node.'
                    : 'No inference recording files found for the current camera configuration.'}
                  showCamera
                  showVideoMeta
                  groupByCamera
                  pageSize={10}
                  cameraOptions={cameras.map((camera) => ({
                    key: camera.key,
                    label: camera.name ?? camera.key,
                  }))}
                  onUploadSelected={
                    canUpload && supportsInferenceUpload
                      ? (ids) => openUpload('inference_recording', ids, visibleInferenceFiles)
                      : undefined
                  }
                  uploadDisabled={isUploading}
                  onSelectionChange={setSelectedInferenceUploadFileIds}
                  onDeleteLocal={canDeleteLocal && supportsFileDelete ? handleDeleteLocal : undefined}
                  deleteLocalDisabled={isUploading}
                  deletingFileId={deletingFileId}
                />
                {deletedFilesError && <div className={styles.errorBanner}>{deletedFilesError}</div>}
                <FileList
                  files={visibleDeletedInferenceFiles}
                  title="Deleted Inference Recordings"
                  description={DELETED_FILES_RETENTION_NOTICE}
                  emptyText={cameras.length === 0
                    ? 'No cameras are currently configured for this node.'
                    : 'No deleted inference recordings found for the current camera configuration.'}
                  showCamera
                  showVideoMeta
                  groupByCamera
                  showDeletedAt
                  defaultSortKey="deleted_at"
                  pageSize={DELETED_FILES_PAGE_SIZE}
                  cameraOptions={cameras.map((camera) => ({
                    key: camera.key,
                    label: camera.name ?? camera.key,
                  }))}
                  activeCameraKey={effectiveDeletedInferenceCameraKey}
                  onCameraChange={(cameraKey) => {
                    setSelectedDeletedInferenceCameraKey(cameraKey)
                    setDeletedInferencePage(1)
                    setDeletedInferenceFiles([])
                    setDeletedInferenceTotal(0)
                    setDeletedFilesError(null)
                  }}
                  showCameraCounts={false}
                  collapsible
                  expanded={deletedInferenceExpanded}
                  onExpandedChange={setDeletedInferenceExpanded}
                  serverPagination={{
                    page: deletedInferencePage,
                    total: deletedInferenceTotal,
                    onPageChange: setDeletedInferencePage,
                  }}
                />
              </>
            ) : (
              <div className={styles.card}>
                <div className={styles.cardTitle}>Inference Recordings</div>
                <p className={styles.inferenceUnsupportedText}>
                  Agent update required to list inference recording files.
                </p>
              </div>
            )
          )}
          {activeTab === 'model_deploy' && (
            <ModelDeployPanel
              states={modelStates}
              availableModels={nodeAvailableModels}
              commands={commands}
              loading={modelLoading}
              error={modelError}
              canMapModel={canDeployModel && supportsModelDeploy}
              canDeleteMapping={canDeployModel}
              canDeployModel={canDeployModel && supportsModelDeploy}
              canRefreshStatus={canDeployModel}
              supportsModelDeploy={supportsModelDeploy}
              headerActions={(
                <>
                  <button
                    type="button"
                    title="노드에 조회 명령을 보냅니다. 결과는 에이전트 응답 후 반영됩니다"
                    disabled={!canDeployModel || !supportsModelDeploy || modelToolbarBusy !== null}
                    onClick={() => runModelToolbarAction(
                      'refresh',
                      () => handleRefreshModelStatus(null),
                    )}
                    className={styles.secondaryActionButton}
                  >
                    모델 상태 조회 요청
                  </button>
                  <button
                    type="button"
                    aria-label="화면 데이터 새로고침"
                    aria-busy={modelLoading || modelToolbarBusy === 'reload'}
                    title="화면 데이터 새로고침"
                    disabled={modelLoading || modelToolbarBusy !== null}
                    onClick={() => runModelToolbarAction('reload', fetchModelData)}
                    className={cx(styles.secondaryActionButton, styles.modelReloadButton)}
                  >
                    <RefreshCw
                      aria-hidden="true"
                      className={cx(
                        styles.modelReloadIcon,
                        (modelLoading || modelToolbarBusy === 'reload') && styles.modelReloadIconBusy,
                      )}
                    />
                  </button>
                  <GuardianAlphaRestartButton
                    node={node}
                    disabledReason={modelStates.some((state) => (
                      state.deployment_status === 'deploying'
                      || state.pending_target_status === 'queued'
                      || state.pending_target_status === 'running'
                    ))
                      ? '모델 배포가 완료된 후 GuardianAlpha를 재시작할 수 있습니다.'
                      : undefined}
                  />
                </>
              )}
              onRefreshSlot={(slotKey) => handleRefreshModelStatus(slotKey)}
              onMapModel={handleMapModel}
              onDeleteMapping={handleDeleteModelMapping}
              onDeploy={handleDeployModel}
            />
          )}
        </div>
      </div>

      {modal && (
        <CommandModal
          nodeId={node.id}
          commandType={modal}
          files={filteredUploadFilesForModal}
          uploadSourceKind={uploadSourceKind}
          initialUploadFileIds={initialUploadFileIds}
          canDeleteLocal={canDeleteLocal}
          initialCameraKey={modalCameraKey}
          onClose={() => { setModal(null); setModalCameraKey(undefined); setInitialUploadFileIds([]); setUploadCameraKey(undefined) }}
          onSuccess={(msg) => {
            if (modal === 'recording.stop' && modalCameraKey) {
              setPendingStoppingCameras((prev) => new Map(prev).set(modalCameraKey, Date.now()))
            }
            setModal(null)
            setModalCameraKey(undefined)
            setInitialUploadFileIds([])
            setUploadCameraKey(undefined)
            addToast(msg)
            fetchAll()
          }}
        />
      )}

      {showAutoUploadConfig && canManageAutoUpload && node.recording_auto_upload && (
        <RecordingAutoUploadModal
          state={node.recording_auto_upload}
          canDeleteLocal={canDeleteLocal}
          onClose={() => setShowAutoUploadConfig(false)}
          onSave={async (body) => {
            await saveRecordingAutoUpload(body)
            setShowAutoUploadConfig(false)
          }}
        />
      )}

      {showRotateToken && canRotateNodeToken && (
        <TokenRotateModal
          nodeId={node.id}
          nodeName={node.name ?? node.node_key}
          onClose={() => setShowRotateToken(false)}
        />
      )}

      {showAgentUpdate && canUpdateAgent && (
        <AgentUpdateModal
          nodeId={node.id}
          nodeName={node.name ?? node.node_key}
          currentVersion={node.agent_version}
          onClose={() => setShowAgentUpdate(false)}
          onSuccess={(msg) => {
            setShowAgentUpdate(false)
            addToast(msg)
            fetchAll()
          }}
        />
      )}

      <div className={styles.toastStack}>
        {toasts.map((t) => (
          <div key={t.id} className={styles.toast}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  )
}
